import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function SessionDrill({ session, onPickBill }) {
  const [bills, setBills] = useState([]);
  useEffect(() => {
    if (!session) return;
    api.get(`/bills?session_id=${session.session_id}&limit=500`).then((r) => setBills(r.data));
  }, [session]);
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-3 text-sm bg-sand p-3 rounded">
        <div><div className="label-overline text-muted-foreground">Opened</div><div>{session && new Date(session.opened_at).toLocaleString()}</div></div>
        <div><div className="label-overline text-muted-foreground">By</div><div>{session?.opened_by_name}</div></div>
        <div><div className="label-overline text-muted-foreground">Bills</div><div className="font-bold">{bills.length}</div></div>
      </div>
      <div className="border border-border rounded max-h-80 overflow-y-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt sticky top-0"><tr><th className="px-3 py-2 text-left">Bill No</th><th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-left">Pay</th></tr></thead>
          <tbody>
            {bills.map((b) => (
              <tr key={b.bill_id} className="border-t border-border hover:bg-sand cursor-pointer" onClick={() => onPickBill(b)} data-testid={`session-bill-${b.bill_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{b.bill_no}</td>
                <td className="px-3 py-2 text-xs">{new Date(b.created_at).toLocaleTimeString()}</td>
                <td className="px-3 py-2">{b.customer_name || "-"}</td>
                <td className="px-3 py-2 text-right">{b.items.length}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">₹{b.net_total.toFixed(2)}</td>
                <td className="px-3 py-2 uppercase text-xs">{b.payment_method}</td>
              </tr>
            ))}
            {bills.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">No bills in this session.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Reports() {
  const { activeShopId } = useShop();
  const [session, setSession] = useState(null);
  const [daily, setDaily] = useState({ products: [], bill_count: 0 });
  const [periodReport, setPeriodReport] = useState({ products: [], buckets: [], bill_count: 0, total_revenue: 0 });
  const [period, setPeriod] = useState("month"); // "month" | "year"
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [monitor, setMonitor] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sessionDrill, setSessionDrill] = useState(null);
  const [billDrill, setBillDrill] = useState(null);

  const load = async () => {
    const [s, d, m, sess, ord] = await Promise.all([
      api.get(`/reports/session?shop_id=${activeShopId}`),
      api.get(`/reports/daily?shop_id=${activeShopId}`),
      api.get(`/reports/sales-monitor?days=30`),
      api.get(`/sessions?shop_id=${activeShopId}&limit=50`),
      api.get(`/bills?shop_id=${activeShopId}&limit=200`),
    ]);
    setSession(s.data); setDaily(d.data); setMonitor(m.data.data); setSessions(sess.data); setOrders(ord.data);
  };
  useEffect(() => { if (activeShopId) load(); }, [activeShopId]);

  // Load monthly/yearly product report when selection changes
  useEffect(() => {
    if (!activeShopId) return;
    const params = new URLSearchParams({ period, shop_id: activeShopId, year: String(selYear) });
    if (period === "month") params.set("month", String(selMonth));
    api.get(`/reports/products-period?${params.toString()}`)
      .then((r) => setPeriodReport(r.data))
      .catch(() => setPeriodReport({ products: [], buckets: [], bill_count: 0, total_revenue: 0 }));
  }, [activeShopId, period, selYear, selMonth]);

  const days = {};
  monitor.forEach((d) => {
    if (!days[d.day]) days[d.day] = { day: d.day.slice(5), shop1: 0, shop2: 0 };
    days[d.day][d.shop_id] = (days[d.day][d.shop_id] || 0) + d.total;
  });
  const chartData = Object.values(days).sort((a,b)=>a.day.localeCompare(b.day));

  return (
    <div className="p-8 overflow-y-auto" data-testid="reports-page">
      <div className="mb-6">
        <div className="label-overline text-muted-foreground">Analytics</div>
        <h1 className="font-heading text-4xl font-bold text-ink">Reports</h1>
      </div>

      <Tabs defaultValue="session">
        <TabsList className="bg-surface-alt">
          <TabsTrigger value="session" data-testid="tab-session">Today</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily Products</TabsTrigger>
          <TabsTrigger value="period" data-testid="tab-period">Monthly / Yearly</TabsTrigger>
          <TabsTrigger value="monitor" data-testid="tab-monitor">Sales Monitor (30d)</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="session" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            {[
              ["Total", `₹${(session?.total||0).toFixed(2)}`, "kpi-total"],
              ["Cash", `₹${(session?.by_method?.cash||0).toFixed(2)}`, "kpi-cash"],
              ["GPay", `₹${(session?.by_method?.gpay||0).toFixed(2)}`, "kpi-gpay"],
              ["MPay", `₹${(session?.by_method?.mpay||0).toFixed(2)}`, "kpi-mpay"],
              ["Card", `₹${(session?.by_method?.card||0).toFixed(2)}`, "kpi-card"],
            ].map(([l,v,t])=>(
              <Card key={l} className="border-border shadow-none" data-testid={t}>
                <CardContent className="p-4">
                  <div className="label-overline text-muted-foreground">{l}</div>
                  <div className="font-mono text-2xl font-bold mt-1">{v}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-surface-alt">
                    <TableHead>Bill No</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(session?.bills||[]).map((b)=>(
                    <TableRow key={b.bill_id}>
                      <TableCell className="font-mono">{b.bill_no}</TableCell>
                      <TableCell className="text-xs">{new Date(b.created_at).toLocaleTimeString()}</TableCell>
                      <TableCell>{b.customer_name||"-"}</TableCell>
                      <TableCell><span className="uppercase text-xs">{b.payment_method}</span></TableCell>
                      <TableCell className="text-xs">{b.created_by_name}</TableCell>
                      <TableCell className="text-right font-mono">₹{b.net_total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {(!session?.bills || session.bills.length===0) && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No bills today.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-surface-alt"><TableHead>Product</TableHead><TableHead className="text-right">Qty Sold</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                <TableBody>
                  {daily.products.map((p)=>(
                    <TableRow key={p.product_id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-right font-mono">{p.qty}</TableCell>
                      <TableCell className="text-right font-mono">₹{Math.round(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {daily.products.length===0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No products sold today.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="period" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex gap-1" data-testid="period-switch">
                  <button
                    onClick={() => setPeriod("month")}
                    className={`px-4 py-2 rounded text-sm font-medium ${period === "month" ? "bg-terracotta text-white" : "bg-surface-alt"}`}
                    data-testid="period-month"
                  >Monthly</button>
                  <button
                    onClick={() => setPeriod("year")}
                    className={`px-4 py-2 rounded text-sm font-medium ${period === "year" ? "bg-terracotta text-white" : "bg-surface-alt"}`}
                    data-testid="period-year"
                  >Yearly</button>
                </div>
                <div>
                  <div className="label-overline text-muted-foreground mb-1">Year</div>
                  <input
                    type="number"
                    value={selYear}
                    onChange={(e) => setSelYear(Number(e.target.value) || now.getFullYear())}
                    className="w-28 h-9 px-2 border border-border rounded font-mono"
                    data-testid="period-year-input"
                  />
                </div>
                {period === "month" && (
                  <div>
                    <div className="label-overline text-muted-foreground mb-1">Month</div>
                    <select
                      value={selMonth}
                      onChange={(e) => setSelMonth(Number(e.target.value))}
                      className="h-9 px-2 border border-border rounded"
                      data-testid="period-month-select"
                    >
                      {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="ml-auto flex gap-6 text-sm">
                  <div><div className="label-overline text-muted-foreground">Bills</div><div className="font-heading text-xl font-bold" data-testid="period-bill-count">{periodReport.bill_count}</div></div>
                  <div><div className="label-overline text-muted-foreground">Revenue</div><div className="font-heading text-xl font-bold text-terracotta" data-testid="period-revenue">₹{Math.round(periodReport.total_revenue)}</div></div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <div className="font-heading font-bold mb-2">Products sold ({period === "month" ? `${selYear}-${String(selMonth).padStart(2,"0")}` : selYear})</div>
                  <div className="border border-border rounded max-h-[500px] overflow-y-auto scroll-thin">
                    <Table>
                      <TableHeader className="sticky top-0 bg-surface-alt">
                        <TableRow><TableHead>Product</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {periodReport.products.map((p) => (
                          <TableRow key={p.product_id} data-testid={`period-prod-${p.product_id}`}>
                            <TableCell className="font-medium">{p.name}</TableCell>
                            <TableCell className="text-right font-mono">{p.qty}</TableCell>
                            <TableCell className="text-right font-mono text-terracotta font-bold">₹{Math.round(p.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {periodReport.products.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No sales in this period.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div>
                  <div className="font-heading font-bold mb-2">{period === "month" ? "Daily" : "Monthly"} breakdown</div>
                  <div className="border border-border rounded max-h-[500px] overflow-y-auto scroll-thin">
                    <Table>
                      <TableHeader className="sticky top-0 bg-surface-alt">
                        <TableRow><TableHead>{period === "month" ? "Day" : "Month"}</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Total</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {periodReport.buckets.map((b) => (
                          <TableRow key={b.label} data-testid={`period-bucket-${b.label}`}>
                            <TableCell className="font-mono text-xs">{b.label}</TableCell>
                            <TableCell className="text-right font-mono">{b.count}</TableCell>
                            <TableCell className="text-right font-mono">₹{Math.round(b.total)}</TableCell>
                          </TableRow>
                        ))}
                        {periodReport.buckets.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground text-sm">No data.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitor" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-5">
              <div className="font-heading text-lg font-bold mb-3">Last 30 days · Shop1 vs Shop2</div>
              <div style={{ width:"100%", height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                    <XAxis dataKey="day" tick={{fontSize:11}} />
                    <YAxis tick={{fontSize:11}} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="shop1" fill="#E05236" name="Shop 1" />
                    <Bar dataKey="shop2" fill="#4A7C59" name="Shop 2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-surface-alt">
                    <TableHead>Session</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Closed</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">Total Sales</TableHead>
                    <TableHead className="text-right">Cash</TableHead>
                    <TableHead className="text-right">Counted</TableHead>
                    <TableHead className="text-right">Diff</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.session_id} data-testid={`session-row-${s.session_id}`} className="cursor-pointer hover:bg-sand" onClick={() => setSessionDrill(s)}>
                      <TableCell className="font-mono text-xs">#{s.session_id.slice(-6)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{s.shop_id}</Badge></TableCell>
                      <TableCell className="text-xs">{new Date(s.opened_at).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">{s.closed_at ? new Date(s.closed_at).toLocaleString() : "-"}</TableCell>
                      <TableCell className="text-xs">{s.opened_by_name}</TableCell>
                      <TableCell>
                        <Badge variant={s.status === "open" ? "outline" : "secondary"} className={s.status === "open" ? "border-moss text-moss" : ""}>
                          {s.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">₹{s.opening_cash.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">{s.summary?.total_sales != null ? `₹${s.summary.total_sales.toFixed(2)}` : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{s.summary?.by_method?.cash != null ? `₹${s.summary.by_method.cash.toFixed(2)}` : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{s.closing_cash != null ? `₹${s.closing_cash.toFixed(2)}` : "-"}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${s.difference == null ? "" : s.difference === 0 ? "text-moss" : s.difference < 0 ? "text-destructive" : "text-ochre"}`}>
                        {s.difference != null ? `₹${s.difference.toFixed(2)}` : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {sessions.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No sessions yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-surface-alt">
                  <TableHead>Bill No</TableHead><TableHead>Date</TableHead><TableHead>Shop</TableHead>
                  <TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Pay</TableHead>
                  <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(orders || []).map((b) => (
                    <TableRow key={b.bill_id} className="cursor-pointer hover:bg-sand" onClick={() => setBillDrill(b)} data-testid={`order-row-${b.bill_id}`}>
                      <TableCell className="font-mono text-xs">{b.bill_no}</TableCell>
                      <TableCell className="text-xs">{new Date(b.created_at).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{b.shop_id}</Badge></TableCell>
                      <TableCell>{b.customer_name || "-"} <span className="text-xs text-muted-foreground">{b.customer_phone}</span></TableCell>
                      <TableCell className="text-xs">{b.items.length}</TableCell>
                      <TableCell className="text-xs uppercase">{b.payment_method}</TableCell>
                      <TableCell className="text-right font-mono font-bold">₹{b.net_total.toFixed(2)}</TableCell>
                      <TableCell><Badge variant={b.status==="refunded"?"destructive":"outline"} className={b.status==="completed"?"border-moss text-moss":""}>{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {(!orders || orders.length === 0) && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No orders.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Session drill-down: bills in this session */}
      <Dialog open={!!sessionDrill} onOpenChange={(o) => !o && setSessionDrill(null)}>
        <DialogContent className="max-w-3xl" data-testid="session-drill-dialog">
          <DialogHeader><DialogTitle className="font-heading">Session #{sessionDrill?.session_id?.slice(-6)} — {sessionDrill?.shop_id}</DialogTitle></DialogHeader>
          <SessionDrill session={sessionDrill} onPickBill={setBillDrill} />
        </DialogContent>
      </Dialog>

      {/* Bill detail */}
      <Dialog open={!!billDrill} onOpenChange={(o) => !o && setBillDrill(null)}>
        <DialogContent className="max-w-2xl" data-testid="bill-drill-dialog">
          <DialogHeader><DialogTitle className="font-heading">Bill {billDrill?.bill_no}</DialogTitle></DialogHeader>
          {billDrill && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm bg-sand p-3 rounded">
                <div><div className="label-overline text-muted-foreground">Customer</div><div className="font-medium">{billDrill.customer_name || "-"}</div><div className="text-xs">{billDrill.customer_phone}</div></div>
                <div><div className="label-overline text-muted-foreground">Payment</div><div className="font-medium uppercase">{(billDrill.payments||[]).map(p=>`${p.method} ₹${p.amount}`).join(" + ") || billDrill.payment_method}</div></div>
                <div><div className="label-overline text-muted-foreground">When</div><div className="text-xs">{new Date(billDrill.created_at).toLocaleString()}</div></div>
                <div><div className="label-overline text-muted-foreground">By</div><div className="text-xs">{billDrill.created_by_name}</div></div>
              </div>
              <table className="w-full text-sm border border-border rounded">
                <thead className="bg-surface-alt"><tr><th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-right">MRP</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Amount</th></tr></thead>
                <tbody>
                  {billDrill.items.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1">{it.name}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{it.mrp.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right">{it.qty}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{it.rate.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{it.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="font-heading font-bold">Net Total</span>
                <span className="font-mono text-2xl font-bold text-terracotta">₹{billDrill.net_total.toFixed(2)}</span>
              </div>
              {billDrill.points_earned ? <div className="text-xs text-moss">Loyalty earned: +{billDrill.points_earned} pts (balance now {billDrill.points_balance_after})</div> : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
